import React, { useState } from "react";
import { logoDarkImage } from "../../shared/images";
import { useRegisterWaitlist } from "../../queries/auth";
import { toast } from "react-toastify";
import WaitlistSuccessModal from "./components/WaitlistSuccessModal";
import useIsMobile from "../../utils/detectDevice";
import { ButtonSize, PrimaryCTAButton } from "../../shared/CTAButtonComponents";
import { ArrowRight } from "lucide-react";
import { Input } from "../../shared/InputField";
import { trackEvent } from "../../utils/AmpAnalytics";

export const WaitlistTopBar = () => {
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const { isMobile } = useIsMobile();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");
  const { mutate, isLoading } = useRegisterWaitlist();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(""); // Clear previous errors

    if (!email) {
      setEmailError("Email is required");
      // Focus the email input for accessibility
      const el = document.getElementById("waitlist-email") as HTMLInputElement | null;
      el?.focus();
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      const el = document.getElementById("waitlist-email") as HTMLInputElement | null;
      el?.focus();
      return;
    }

    mutate(
      { data: { email } },
      {
        onSuccess: (data) => {
          setSuccessEmail(email);
          setIsSuccessModalOpen(true);
          setEmail("");
          setIsInputVisible(false);
          console.log("Data", data);
        },
        onError: (err: any) => {
          console.log("Error", err);
          toast.error(err?.response?.data?.message || "Something went wrong");
        },
      },
    );
  };

  return (
    <>
      <div className="bg-gray-100 p-4 md:px-6 pb-1 rounded-t-lg text-center border-b border-gray-200 ">
        {/* <span className="inline-flex items-center gap-1 mb-2 px-3 py-1 rounded-full text-[10px] font-bold text-[#6d8fb3] border border-[#6d8fb3] uppercase tracking-widest bg-white">
          <span className="w-1.5 h-1.5 rounded-full bg-[#6d8fb3]" />
          Private Beta
        </span> */}

        <h3 className="text-2xl font-bold text-gray-700 mb-2 mt-4">
          Invite-only access
        </h3>

        <p className="text-xs text-gray-500 mx-auto max-w-[320px] font-semibold">
          {/* Join the waitlist to get early access to Hissa&apos;s Marketplace
          Platform. */}
          {/* We're currently onboarding teams from select companies. Join the waitlist to get notified. */}
          Request access to join Hissa. We'll notify you once approved.
        </p>

        <div className="flex justify-center items-center h-11 my-4 mx-auto">
          {!isInputVisible ? (
            <PrimaryCTAButton
              event-name="Email Continue"
              className={`${"w-auto px-4"} text-center px-auto `}
              buttonSize={ButtonSize.MEDIUM}
              onClick={() => setIsInputVisible(true)}
              type="button"
            >
              <span className="flex justify-center items-center gap-2 text-sm">
                Join Waitlist
                <ArrowRight className="h-4 w-4 text-white font-semibold" />
              </span>
            </PrimaryCTAButton>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className=" flex flex-col w-full mx-4 max-w-[320px]"
            >
              <div className="flex flex-row gap-2">
                <Input
                  id="waitlist-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail((e as React.ChangeEvent<HTMLInputElement>).target.value);
                    if (emailError) setEmailError(""); // Clear error when user starts typing
                  }}
                  placeholder="Email Address"
                  className="py-0 focus:border-2"
                  aria-invalid={Boolean(emailError)}
                  autoFocus
                />

                <PrimaryCTAButton
                  event-name="Email Continue"
                  className={`${"w-auto px-4"} text-center px-auto `}
                  buttonSize={ButtonSize.SMALL}
                  onClick={() =>{
                    trackEvent("Adding user to waitlist");
                    setIsInputVisible(true)}}
                  loading={isLoading}
                  type="submit"
                >
                  <p className="w-full text-sm leading-0"> Submit</p>
                </PrimaryCTAButton>
              </div>
              {emailError && (
                <p className="text-red-500 text-xs text-left mt-1">{emailError}</p>
              )}
            </form>
          )}
        </div>
      </div>

      <WaitlistSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        email={successEmail}
      />
    </>
  );
};
